import _ from 'lodash';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Modal } from '../components/design-system/Modal';
import {
  CreditPill,
  CategoryEarnRatePill,
  CardInfoGrid,
  CardInfoFlex,
  CardInfoBlock,
} from '../components';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { CreditCard } from 'data/shared/CreditCard';
import { CreditCardData } from 'data/shared/CreditCardData';

import {
  isQuarterlyCategoriesEarnRate,
  isSelectableCategoriesEarnRate,
  isFixedCategoryGroupEarnRate,
  isAdvancedCategory,
} from 'data/types/utils';
import { CardRewardsEarnRate } from 'data/types/CardData';
import { categoryEmojiMap } from '../data/shared/utils/CategoryEmojiMap.web';
import { AdvancedCategory, SelectableCategoriesEarnRate } from 'data/types';
import { flattenRewardsEarnRates } from 'data/shared/utils';
import {
  getCurrentQuarter,
  getSelectedCategories,
  getSelectedCategoriesForRate,
} from 'data/shared/hooks/useBestEarnRatesV2';
import { useParamStatePointValues } from './useURLParamPointValues';
import { EditPointValues } from '../components/EditPointValues';
import { EarnRateConditionsMessage } from '../components/EarnRateConditionsMessage';
import { CreditCardImage } from '../components/design-system/CreditCardImage';
import { CardDetailsPanel } from '../components/CardDetailsPanel';
import { ActionLinkButton } from '../components/design-system/ActionLinkButton';
import { ActionButton } from '../components/design-system/ActionButton';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';

// Helper function to get category emoji
const getCategoryEmoji = (
  category: string
): React.ReactNode | string | undefined => {
  // Try exact match first
  if (categoryEmojiMap[category]) {
    return categoryEmojiMap[category];
  }

  // Try parent categories by removing segments from right to left
  const segments = category.split('.');
  while (segments.length > 1) {
    segments.pop();
    const parentCategory = segments.join('.');
    if (categoryEmojiMap[parentCategory]) {
      return categoryEmojiMap[parentCategory];
    }
  }

  // Return fallback if no match found
  return '📦';
};

// Helper function to extract end date from earn rate description
const extractEndDate = (description: string | null): string | null => {
  if (!description) return null;

  // Look for various end date patterns in the description
  const endDatePatterns = [
    /valid through (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /through (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /until (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /expires (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /expires (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /continues beyond (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /excluded starting (\d{1,2}\/\d{1,2}\/\d{4})/i,
    /starting (\d{1,2}\/\d{1,2}\/\d{4})/i,
  ];

  for (const pattern of endDatePatterns) {
    const match = description.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
};

// Helper function to format signup bonus by aggregating rewards by type
const formatSignupBonus = (signupBonus: any[] | null | undefined): string => {
  if (!signupBonus || signupBonus.length === 0) {
    return 'None';
  }

  // Aggregate rewards by type across all tiers
  const aggregatedRewards: Record<string, number> = {};

  signupBonus.forEach((tier) => {
    if (tier.rewards && Array.isArray(tier.rewards)) {
      tier.rewards.forEach((reward: any) => {
        if (reward.amount && reward.value_unit) {
          const unit = reward.value_unit.toLowerCase();
          const key = unit === 'dollars' ? 'cash_back' : unit;

          if (aggregatedRewards[key]) {
            aggregatedRewards[key] += reward.amount;
          } else {
            aggregatedRewards[key] = reward.amount;
          }
        }
      });
    }
  });

  // Format the aggregated rewards into a readable string
  const formattedRewards: string[] = [];

  Object.entries(aggregatedRewards).forEach(([type, amount]) => {
    if (type === 'cash_back') {
      formattedRewards.push(`$${amount.toLocaleString()} cash back`);
    } else if (type === 'points') {
      formattedRewards.push(`${amount.toLocaleString()} points`);
    } else if (type === 'miles') {
      formattedRewards.push(`${amount.toLocaleString()} miles`);
    } else if (type === 'nights') {
      formattedRewards.push(`${amount.toLocaleString()} free nights`);
    } else {
      // For other reward types, use the original unit
      formattedRewards.push(`${amount.toLocaleString()} ${type}`);
    }
  });

  if (formattedRewards.length === 0) {
    return 'None';
  }

  if (formattedRewards.length === 1) {
    return `Up to ${formattedRewards[0]}`;
  }

  // Join multiple rewards with "and"
  const lastReward = formattedRewards.pop();
  return `Up to ${formattedRewards.join(', ')} and ${lastReward}`;
};

type CardModalSetter = (cardOrState: CreditCard | { card: CreditCard; walletIndex?: number } | undefined) => void;

export const useCardModal = ({
  handleRemoveCard,
  handleAddCard,
  selectedCards = [],
  onSelectCategoriesForCard,
  currentSelectedCategories,
  promotionalTypes,
  setPromotionalTypes,
  resetPromotionalTypes,
}: {
  handleRemoveCard?: (_: CreditCard, walletIndex?: number) => void;
  handleAddCard?: (_: CreditCard) => void;
  selectedCards?: CreditCard[];
  onSelectCategoriesForCard?: (
    card: CreditCard,
    selectedCategories: string[][][],
    walletIndex?: number
  ) => void;
  currentSelectedCategories?: Partial<Record<string, string[][][]>> | (string[][][] | undefined)[];
  promotionalTypes?: PromotionalType[];
  setPromotionalTypes?: (types: PromotionalType[]) => void;
  resetPromotionalTypes?: () => void;
}): [
  null | React.ReactElement,
  CardModalSetter
] => {
  const [showCardModalState, setShowCardModalState] = useState<
    { card: CreditCard; walletIndex?: number } | undefined
  >(undefined);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );
  const [showEditPointValues, setShowEditPointValues] = useState(false);

  const setShowCardModal = useCallback<CardModalSetter>(
    (cardOrState) => {
      if (cardOrState === undefined || typeof cardOrState === 'string') {
        setShowCardModalState(cardOrState ? { card: cardOrState } : undefined);
      } else {
        setShowCardModalState(cardOrState);
      }
    }, []
  );

  const showCardModalCard = showCardModalState?.card;
  const showCardModalWalletIndex = showCardModalState?.walletIndex;

  // Memoize the cards array to prevent infinite re-renders
  const cardsForPointValues = useMemo(
    () => (showCardModalCard ? [showCardModalCard] : []),
    [showCardModalCard]
  );

  // Get point values for the current card
  const { pointValues, setPointValues, resetPointValues } =
    useParamStatePointValues(cardsForPointValues, true);

  if (!showCardModalCard) {
    return [null, setShowCardModal];
  }

  const cardInfo = CreditCardData[showCardModalCard];

  // Check if card is in wallet
  const isInWallet = selectedCards.includes(showCardModalCard);

  // Look up current categories for the displayed card
  const currentCardCategories = Array.isArray(currentSelectedCategories)
    ? (showCardModalWalletIndex !== undefined
      ? currentSelectedCategories[showCardModalWalletIndex]
      : currentSelectedCategories[selectedCards.indexOf(showCardModalCard)])
    : currentSelectedCategories?.[showCardModalCard];

  // Get selectable categories for this card
  const selectableRates = (cardInfo.rewards_earn_rates?.filter(
    (rate) =>
      isSelectableCategoriesEarnRate(rate) &&
      rate.category === 'selectable_categories'
  ) || []) as SelectableCategoriesEarnRate[];

  // Get quarterly categories for this card
  const quarterlyRates = (cardInfo.rewards_earn_rates?.filter(
    (rate) =>
      isQuarterlyCategoriesEarnRate(rate) &&
      rate.category === 'quarterly_categories'
  ) || []) as SelectableCategoriesEarnRate[];

  const fixedCategories = (cardInfo.rewards_earn_rates?.filter(
    (rate) =>
      isFixedCategoryGroupEarnRate(rate) &&
      rate.category === 'fixed_category_group'
  ) || []) as SelectableCategoriesEarnRate[];

  // Initialize selected categories if they don't exist yet
  if (
    (selectableRates.length > 0 || quarterlyRates.length > 0) &&
    onSelectCategoriesForCard &&
    currentSelectedCategories &&
    !currentCardCategories
  ) {
    // Initialize with proper multi-rate structure
    const initialSelections: string[][][] = [];

    // Initialize selectable categories using getSelectedCategories for each rate
    selectableRates.forEach((rate, rateIndex) => {
      // Use getSelectedCategories with empty user selections to get defaults
      const { selectedCategories } = getSelectedCategories({
        userSelectedCategories: [],
        reward: rate,
      });

      initialSelections[rateIndex] = selectedCategories;
    });

    // Call the callback with initial selections for all rates
    onSelectCategoriesForCard(showCardModalCard, initialSelections, showCardModalWalletIndex);
  }

  return [
    <Modal
      isOpen={true}
      handleClose={() => setShowCardModal(undefined)}
      key={`${cardInfo.name} - ${cardInfo.rewardsType}`}
      extraFooterButtons={
        <>
          {isInWallet && handleRemoveCard && (
            <ActionButton
              variant="danger"
              onClick={() => {
                handleRemoveCard(showCardModalCard, showCardModalWalletIndex);
                setShowCardModal(undefined);
              }}
            >
              Remove from wallet
            </ActionButton>
          )}
          {isInWallet && handleAddCard && (
            <ActionLinkButton
              onClick={() => {
                handleAddCard(showCardModalCard);
                setShowCardModal(undefined);
              }}
              variant="secondary"
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                backgroundColor: '#e5e7eb',
                color: '#4b5563',
              }}
            >
              Add another
            </ActionLinkButton>
          )}
          {!isInWallet && handleAddCard && (
            <ActionLinkButton
              onClick={() => {
                handleAddCard(showCardModalCard);
                setShowCardModal(undefined);
              }}
              variant="secondary"
              style={{
                padding: '8px 16px',
                fontSize: '14px',
              }}
            >
              Add to wallet
            </ActionLinkButton>
          )}
        </>
      }
    >
      <div className="card-modal-container">
        {/* Left Panel - Card Image and Actions */}
        <div className="card-modal-left-panel">
          <CreditCardImage
            imageName={cardInfo.imageName}
            alt={cardInfo.name}
            width="100%"
            maxWidth={200}
            height="auto"
            maxHeight={150}
            style={{
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              marginBottom: '20px',
              margin: '0 auto 20px auto',
              minHeight: 100,
            }}
          />

          {cardInfo.availability && cardInfo.availability !== 'available' ? (
            <div style={{
              margin: '8px 16px',
              padding: '10px 16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#888',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
            }}>
              {cardInfo.availability === 'discontinued'
                ? 'This card has been discontinued'
                : cardInfo.availability === 'coming_soon'
                ? 'This card is coming soon'
                : cardInfo.availability === 'waitlist'
                ? 'This card is currently waitlist-only'
                : 'This card is not currently available for new applications'}
            </div>
          ) : (
            <>
              {cardInfo.apply_link && (
                <div style={{ margin: '8px 16px' }}>
                  <a
                    href={cardInfo.apply_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      padding: '12px 24px',
                      backgroundColor: '#49D594',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      color: 'black',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                      fontWeight: 300,
                      fontFamily: 'Merriweather',
                      fontSize: '16px',
                      textAlign: 'center',
                    }}
                  >
                    Apply now
                  </a>
                </div>
              )}

              {cardInfo.learn_more_link && (
                <div style={{ marginBottom: '8px' }}>
                  <a
                    href={cardInfo.learn_more_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    Learn more
                  </a>
                </div>
              )}

              {cardInfo.apply_link && (
                <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                  at {cardInfo.bank_name || cardInfo.issuer || 'the issuer'}'s
                  website
                </p>
              )}
            </>
          )}
        </div>

        {/* Right Panel - Card Details */}
        <CardDetailsPanel
          cardInfo={cardInfo}
          currentSelectedCategories={currentCardCategories}
          onSelectCategoriesForCard={
            onSelectCategoriesForCard
              ? (selections) =>
                  onSelectCategoriesForCard(showCardModalCard, selections, showCardModalWalletIndex)
              : undefined
          }
          pointValues={pointValues}
          setShowEditPointValues={setShowEditPointValues}
          showEditPointValues={showEditPointValues}
          setPointValues={setPointValues}
          resetPointValues={resetPointValues}
          promotionalTypes={promotionalTypes}
          setPromotionalTypes={setPromotionalTypes}
          resetPromotionalTypes={resetPromotionalTypes}
        />
      </div>

      {/* Edit Point Values Modal */}
      {showEditPointValues && (
        <EditPointValues
          handleClose={() => setShowEditPointValues(false)}
          cards={showCardModalCard ? [showCardModalCard] : []}
          pointValues={pointValues}
          setPointValues={setPointValues}
          resetPointValues={resetPointValues}
          promotionalTypes={promotionalTypes}
          setPromotionalTypes={setPromotionalTypes}
          resetPromotionalTypes={resetPromotionalTypes}
        />
      )}
    </Modal>,
    setShowCardModal,
  ];
};
